import { FC } from "react";

interface StarRatingProps {
  rating: number;
  maxRating?: number;
  size?: "xs" | "sm" | "md" | "lg";
  showRating?: boolean;
  className?: string;
}

/** lucide-react `Star` ikonunun path'i — görsel birebir aynı kalsın diye kopyalandı. */
const STAR_PATH =
  "M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z";

const SIZES = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
} as const;

/**
 * Tek <svg> — yıldız başına iki ayrı <Star> bileşeni yerine.
 *
 * Eski sürüm kısmi doldurma için yıldız başına bir sarmalayıcı div + gri taban
 * SVG + kırpıcı div + sarı SVG basıyordu: 5 yıldız = ~30 düğüm. Ana sayfada 84
 * kart × 10 SVG = 840 SVG, sayfadaki 1.236 SVG'nin üçte ikisi.
 *
 * Burada tüm satır tek bir SVG: gri katman + <clipPath> ile yüzdesine göre
 * kırpılmış sarı katman. Geometri lucide'ın kendi path'i olduğu için görsel
 * sonuç değişmiyor.
 *
 * clipPath id'si oranın yüzdesinden türetiliyor: aynı orana sahip kartlar aynı
 * id'yi paylaşır. Tanımlar birebir aynı olduğu için çakışma zararsız — üstelik
 * tarayıcı tarafında tekrar eden tanım sayısını da düşürür. (useId kullanılmadı:
 * bu bileşen sunucu ağacından da render ediliyor.)
 */
const StarRating: FC<StarRatingProps> = ({
  rating,
  maxRating = 5,
  size = "sm",
  showRating = false,
  className = "",
}) => {
  const starSize = SIZES[size];
  const width = starSize * maxRating;

  // 0–100 arası, tam sayıya yuvarlanmış doluluk oranı.
  const fillPercent = Math.max(
    0,
    Math.min(100, Math.round((rating / maxRating) * 100)),
  );
  const clipId = `star-clip-${maxRating}-${fillPercent}`;

  const stars = Array.from({ length: maxRating });
  const paths = (
    <>
      {stars.map((_, index) => (
        <path key={index} d={STAR_PATH} transform={`translate(${index * 24} 0)`} />
      ))}
    </>
  );

  // Uç durumlar için kısayol: puan yoksa (0) sarı katman, tamsa (100) gri katman
  // ve kırpma tanımı hiç basılmıyor. Ana sayfada puanı 0 olan kart çok — o
  // kartlar 16 düğüm yerine 6 düğümle iniyor.
  const needsEmptyLayer = fillPercent < 100;
  const needsFilledLayer = fillPercent > 0;
  const needsClip = needsFilledLayer && fillPercent < 100;

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <svg
        width={width}
        height={starSize}
        viewBox={`0 0 ${maxRating * 24} 24`}
        fill="none"
        aria-hidden="true"
        className="shrink-0"
      >
        {needsClip && (
          <defs>
            <clipPath id={clipId}>
              <rect x="0" y="0" width={`${fillPercent}%`} height="24" />
            </clipPath>
          </defs>
        )}

        {/* Boş yıldızlar — gri kontur (eski `text-gray-300` görünümü) */}
        {needsEmptyLayer && (
          <g stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {paths}
          </g>
        )}

        {/* Dolu kısım — orana göre kırpılmış sarı katman */}
        {needsFilledLayer && (
          <g
            clipPath={needsClip ? `url(#${clipId})` : undefined}
            fill="#facc15"
            stroke="#facc15"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {paths}
          </g>
        )}
      </svg>

      {showRating && (
        <span className="text-sm text-gray-600 ml-1">
          ({rating}/{maxRating})
        </span>
      )}
    </div>
  );
};

export default StarRating;
